import { useEffect, useMemo, useState } from 'react';

const useData = ({ getAllAnyWithFilters, name, initRowsPerPage = 20, initialFilters = '', orderBy, isEnabled = true }) => {
  const storedPage = sessionStorage.getItem(`${name}_currentPage`);
  const storedCache = sessionStorage.getItem(`${name}_dataCache`);
  const storedTokens = sessionStorage.getItem(`${name}_tokenHistory`);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(storedPage ? parseInt(storedPage) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(initRowsPerPage);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [tokenHistory, setTokenHistory] = useState(storedTokens ? JSON.parse(storedTokens) : ['']);
  const [pageDataCache, setPageDataCache] = useState(storedCache ? JSON.parse(storedCache) : { 0: [] });
  const [continuationToken, setContinuationToken] = useState('');
  const [order, setOrder] = useState(orderBy || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);

  const sliceRows = useMemo(() => ({ start: page * rowsPerPage, end: page * rowsPerPage + rowsPerPage }), [page, rowsPerPage]);

  useEffect(() => {
    sessionStorage.setItem(`${name}_currentPage`, page.toString());
    sessionStorage.setItem(`${name}_dataCache`, JSON.stringify(pageDataCache));
    sessionStorage.setItem(`${name}_tokenHistory`, JSON.stringify(tokenHistory));
  }, [page, pageDataCache, tokenHistory, name]);

  useEffect(() => {
    if (isEnabled && !hasInitialData && (!pageDataCache[page] || pageDataCache[page].length === 0)) {
      gettingData();
    } else if (pageDataCache[page]) {
      setRows(pageDataCache[page]);
    }
    // eslint-disable-next-line
  }, [isEnabled]);

  const gettingData = async () => {
    setIsLoading(true);
    try {
      const result = await getAllAnyWithFilters({
        queryKey: [name, { filters, rowsPerPage, order }],
      });
      if (result && result.data) {
        const { continuationToken, count, data } = result;
        setContinuationToken(continuationToken);
        setTokenHistory(['', continuationToken]);
        setCount(count);
        setRows(data);
        setPageDataCache(prev => ({
          ...prev,
          0: data
        }));
        setRowsPerPage(rowsPerPage);
        setPage(0);
        setHasInitialData(true);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsError(true);
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    sessionStorage.removeItem(`${name}_currentPage`);
    sessionStorage.removeItem(`${name}_dataCache`);
    sessionStorage.removeItem(`${name}_tokenHistory`);
    setPageDataCache({});
    setHasInitialData(false);
    getDataWithFilters('');
  };

  const getDataNextPage = async (newPage) => {
    if (pageDataCache[newPage] && pageDataCache[newPage].length > 0) {
      setRows(pageDataCache[newPage]);
      setPage(newPage);
      return;
    }

    setIsLoading(true);
    try {
      const token = newPage > page ? continuationToken : tokenHistory[newPage];

      const result = await getAllAnyWithFilters({
        queryKey: [name, { filters, continuationToken: token, rowsPerPage, order }],
      });

      if (result && result.data) {
        const { continuationToken: newToken, count, data } = result;
        setContinuationToken(newToken);

        const newCache = { ...pageDataCache, [newPage]: data };
        setPageDataCache(newCache);

        if (newPage > page) {
          setTokenHistory(prev => [...prev, newToken]);
        }

        setCount(count);
        setRows(data);
        setPage(newPage);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
    }
  };

  const getDataWithFilters = async (filters) => {
    setIsLoading(true);
    try {
      setFilters(filters);
      const result = await getAllAnyWithFilters({
        queryKey: [name, { filters, rowsPerPage, order }],
      });
      if (result && result.data) {
        const { continuationToken, count, data } = result;
        setContinuationToken(continuationToken);
        setTokenHistory(['', continuationToken]);
        setCount(count);
        setRows(data);
        setPageDataCache({ 0: data });
        setPage(0);
        setHasInitialData(true);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
    }
  };

  const getDataSorted = async (newOrder) => {
    setIsLoading(true);
    try {
      setOrder(newOrder);
      const result = await getAllAnyWithFilters({
        queryKey: [name, { filters, rowsPerPage, order: newOrder }],
      });
      if (result && result.data) {
        const { continuationToken, count, data } = result;
        setContinuationToken(continuationToken);
        setTokenHistory(['', continuationToken]);
        setCount(count);
        setRows(data);
        setPageDataCache({ 0: data });
        setPage(0);
        setHasInitialData(true);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
    }
  };

  const topCategories = useMemo(() => {
    const categoryCount = {};

    rows.forEach(post => {
      const category = post.Category;
      if (category) {
        categoryCount[category] = (categoryCount[category] || 0) + 1;
      }
    });

    return Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([name]) => name);
  }, [rows]);

  return {
    rows,
    page,
    count,
    isError,
    isLoading,
    sliceRows,
    getDataNextPage,
    getDataWithFilters,
    getDataSorted,
    resetFilters,
    topCategories,
  };
};

export default useData;